/**
 * «Замечание диспетчеру» — нижний лист, не отдельный экран (§2.2, конец
 * раздела: «одна кнопка внизу, открывает нижний лист с текстом»).
 *
 * ─────────────────────────────────────────────────────────────────────────
 * ЭТО НЕ КОММЕНТАРИЙ, И НАЗЫВАТЬСЯ ТАК ОНО НЕ ДОЛЖНО
 * ─────────────────────────────────────────────────────────────────────────
 * Отправляется в `POST /v1/vehicles/{id}/report`, то есть заводит НАРЯД в
 * ремонтной доске, а не строчку в блокноте. У наряда есть последствия,
 * которые `fleet` применяет сам (`internal/biz/work_order.go`):
 * `electrical`/`controller` (или три любые открытые заявки) немедленно
 * переводят доступный байк в `faulty` и заводят задачу «забрать». Поэтому
 * последствие названо ДО отправки, а категория по умолчанию — «Прочее»,
 * единственная безобидная.
 */
import { useState } from 'react'
import {
  REPORT_CATEGORIES,
  reportCategoryLabel,
  reportConsequence,
  useReportProblem,
  type ReportCategory,
} from '@/api/vehicles'
import { BigButton, BottomSheet, Notice } from '@/components/ui'

export function ReportSheet({
  vehicleId,
  open,
  onClose,
}: {
  vehicleId: string
  open: boolean
  onClose: () => void
}) {
  const report = useReportProblem()
  const [category, setCategory] = useState<ReportCategory>('other')
  const [note, setNote] = useState('')
  const [sent, setSent] = useState(false)

  function close() {
    setCategory('other')
    setNote('')
    setSent(false)
    onClose()
  }

  const consequence = reportConsequence(category)

  return (
    <BottomSheet open={open} onClose={close}>
      {sent ? (
        <Notice tone="ok">
          Замечание принято. Уйдёт сразу, а без сети — как появится связь.
        </Notice>
      ) : (
        <>
          <h2 className="mb-2 text-lg font-medium">Замечание диспетчеру</h2>
          <p className="text-sm opacity-70">Заявка попадёт в ремонтную доску диспетчера.</p>

          <div className="mt-2 flex flex-wrap gap-2">
            {REPORT_CATEGORIES.map((c) => (
              <button
                key={c}
                aria-pressed={category === c}
                onClick={() => {
                  setCategory(c)
                }}
                className={`min-h-11 rounded-full px-4 text-sm font-medium transition ${
                  category === c
                    ? 'bg-emerald-500 text-black'
                    : 'border border-white/20 text-white/80 active:bg-white/10'
                }`}
              >
                {reportCategoryLabel[c]}
              </button>
            ))}
          </div>

          {consequence ? (
            <div className="mt-2">
              <Notice tone="warn">{consequence}</Notice>
            </div>
          ) : null}

          <label className="mt-3 block text-sm opacity-70" htmlFor="report-note">
            Что заметили
          </label>
          <textarea
            id="report-note"
            rows={3}
            className="mt-1 w-full rounded-lg border border-white/20 bg-transparent px-3 py-2"
            value={note}
            onChange={(e) => {
              setNote(e.target.value)
            }}
            placeholder="Например: заднее колесо восьмёркой"
            autoFocus
          />

          <div className="mt-3 flex gap-2">
            <BigButton
              tone="primary"
              busy={report.isPending}
              disabled={note.trim() === ''}
              onClick={() => {
                report.mutate(
                  { vehicleId, category, note: note.trim() },
                  { onSuccess: () => { setSent(true) } },
                )
              }}
            >
              Отправить
            </BigButton>
            <BigButton tone="neutral" onClick={close}>
              Отмена
            </BigButton>
          </div>

          {report.isError ? (
            <div className="mt-2">
              <Notice tone="error">
                Замечание не сохранено на устройстве. Проверьте, что браузеру
                разрешено хранить данные, и повторите.
              </Notice>
            </div>
          ) : null}
        </>
      )}
    </BottomSheet>
  )
}
